import { defineStore } from 'pinia';
import { ref } from 'vue';
import api, { ApiError } from '@/api2/client';
import {
  updateStagedItems, dropStagedItem, discardStagedItems, scanCdaDocument,
} from '@/api2/cdaDocuments';

/**
 * Save did not commit because a change the clinician made to an extracted item
 * failed while Save waited for it (`settled`). The page tells that apart from a
 * commit that failed: there the fix is the change, not trying the Save again.
 */
export class ReviewChangeNotSaved extends Error {
  constructor() {
    super('A change to an extracted item could not be saved, so the CDA was not saved.');
    this.name = 'ReviewChangeNotSaved';
    this.code = 'review_change_failed';
  }
}

/**
 * What a scanned C-CDA proposes to add to the chart — its staged items
 * (`GET /cda-documents/{id}/staged-items`) — for the one C-CDA open on the CDA
 * page, keyed by its uid. pacmny-be built every C-CDA's staged items into
 * `getAllData`; the port reads them only for the document the clinician has
 * open, because that is the only side bar on screen and each read is an audited
 * read of an outside record. `chartCdasForGetAllData` lays them over that
 * document's row (clinicalBridge `withStagedItems`).
 *
 * - **Read once per document**, the first time its row is read while open, and
 *   again after `invalidate()` (`login/updateApplicationModel`, which follows
 *   an upload, a save and a delete). Until the re-read lands the items stay on
 *   screen but are marked stale, and a stale set does not count as shown: Save
 *   waits for the set it would commit.
 * - **A failed read is remembered as failed**, not as "nothing to review", and
 *   is not retried on every render.
 * - **An answer for a read that was abandoned is dropped.**
 *
 * **The review writes through here too** (D6 default 2026-10-21: Save is always
 * available, and what the document left undated or uncoded is a note on the
 * item, which the clinician may act on first). `correct`, `drop`, `discardAll`
 * and `rescan` are the four things a clinician does about it. The answer to a
 * correction or a drop is the set that remains, with its notes read again, so
 * the entry is settled with it and no second read is needed; a write that fails
 * is said on the entry (`errors`) and changes nothing on screen. Each makes any
 * read still under way stale, so an older answer cannot land over a newer set.
 *
 * `errors` is shown by two pages: CdaStagedItemNotes says the failure of the
 * first three, which it hosts, and CdaContentBody says that of `rescan`, because
 * the C-CDA it reads again has no staged set and so no notes on screen.
 *
 * **Save waits for the review's changes** (`hold`, `settled`). Save commits what
 * the API holds, so a change still on its way - a date PATCH, an item being left
 * out - is not in it: a Save confirmed meanwhile committed the old date, or the
 * item the clinician had just left out. Each change in the air is a *hold* on
 * its document: every write taken here, and every date the page keeps for the
 * write under way (`CdaStagedItemNotes`, which takes one while it waits its
 * turn). `settled(uid)` is the promise of there being none left.
 */
export const useChartCdaStagedItemsStore = defineStore('chartCdaStagedItems', () => {
  /** uid -> `{items, failed, stale}` */
  const entries = ref({});

  /** uid -> what the last review write on that C-CDA said when it failed. */
  const errors = ref({});

  // Bookkeeping, not state: these change while a getter is being evaluated,
  // and reactive writes there would re-trigger it.
  let inflight = {};
  let requested = new Set();

  /** uid -> how many review changes are in the air (see `hold`). */
  let holds = {};
  /** uid -> who is waiting for the last of them. */
  let idleWaiters = {};
  /** uid -> how many review changes have failed, ever; `settled` compares it before and after its wait. */
  let failures = {};
  /** Counts the `clear()`s: a hold taken before one does not undo a hold taken after it. */
  let generation = 0;

  function settle(uid, value) {
    entries.value = { ...entries.value, [uid]: value };
  }

  /** Read the document's staged items now (or join the read under way). Never rejects. */
  function load(uid) {
    if (typeof uid !== 'string' || uid === '') return Promise.resolve();
    if (inflight[uid]) return inflight[uid].promise;

    const entry = { promise: null };

    entry.promise = api.get(`/cda-documents/${uid}/staged-items`)
      .then((items) => {
        if (inflight[uid] !== entry) return;
        delete inflight[uid];
        settle(uid, { items: items || {}, failed: false, stale: false });
      })
      .catch((error) => {
        if (inflight[uid] !== entry) return;
        delete inflight[uid];
        // A read the page never asked for in so many words must not surface as
        // an unhandled rejection; the page says the items could not be loaded.
        if (!(error instanceof ApiError) && process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('[chartCdaStagedItems] could not load the staged items', error);
        }
        settle(uid, { items: null, failed: true, stale: false });
      });

    inflight[uid] = entry;

    return entry.promise;
  }

  /**
   * Load the document's staged items unless they are loaded, loading or asked
   * for. Called from a getter, so the request is deferred to a microtask.
   */
  function ensureFor(uid) {
    if (typeof uid !== 'string' || uid === '') return;

    const entry = entries.value[uid];

    if (entry && !entry.stale) return;
    if (inflight[uid] || requested.has(uid)) return;

    requested.add(uid);
    Promise.resolve()
      .then(() => load(uid))
      .finally(() => requested.delete(uid));
  }

  /** Every document's staged set may have changed: re-read each the next time it is read. */
  function invalidate() {
    const next = {};
    Object.keys(entries.value).forEach((uid) => {
      next[uid] = { ...entries.value[uid], stale: true };
    });
    entries.value = next;
    inflight = {};
    requested = new Set();
  }

  function clear() {
    entries.value = {};
    errors.value = {};
    inflight = {};
    requested = new Set();

    // A sign-out with a change in the air: nobody is left to wait for it.
    const waiting = Object.values(idleWaiters).flat();
    generation += 1;
    holds = {};
    idleWaiters = {};
    failures = {};
    waiting.forEach((resolve) => resolve());
  }

  // ------------------------------------------------- the changes in the air

  /**
   * Note that a review change on the document is in the air, until the returned
   * function is called (once; again does nothing). Taken by `write` for every
   * change it runs, and by the page for a date it keeps until the change under
   * way is answered - a held date is a change just as much, and Save must not
   * overtake it.
   *
   * @returns {() => void} release
   */
  function hold(uid) {
    holds[uid] = (holds[uid] || 0) + 1;

    const born = generation;
    let released = false;

    return () => {
      if (released) return;
      released = true;

      // Gone with a `clear()`: its count went with it.
      if (born !== generation) return;

      holds[uid] -= 1;
      if (holds[uid] > 0) return;

      delete holds[uid];

      const waiting = idleWaiters[uid] || [];
      delete idleWaiters[uid];
      waiting.forEach((resolve) => resolve());
    };
  }

  /**
   * Where the document's review failures stand now, for `settled` to be asked
   * about the ones that come after (a Save takes it when it is pressed).
   */
  function failureMark(uid) {
    return failures[uid] || 0;
  }

  /**
   * Wait until no review change on the document is in the air. Resolves `true`
   * when every change it waited for worked (or there was none to wait for), and
   * `false` when one failed since `since` - by default since it was asked: that
   * is a change the clinician made that is not on the item, and a Save that went
   * ahead would say it was. A failure from before is not held against it; the
   * clinician was told then.
   *
   * @param {number} [since] a `failureMark`, taken earlier, to count failures from
   * @returns {Promise<boolean>}
   */
  function settled(uid, since = failureMark(uid)) {
    const idle = holds[uid]
      ? new Promise((resolve) => { (idleWaiters[uid] = idleWaiters[uid] || []).push(resolve); })
      : Promise.resolve();

    return idle.then(() => failureMark(uid) === since);
  }

  // ----------------------------------------------------------- the review's writes

  function setError(uid, message) {
    errors.value = { ...errors.value, [uid]: message };
  }

  function clearError(uid) {
    if (!(uid in errors.value)) return;

    const next = { ...errors.value };
    delete next[uid];
    errors.value = next;
  }

  /**
   * Run one review write. A read under way is made stale first (its answer would
   * be older than this write's), the earlier failure is forgotten, and a failure
   * is kept as words for the page and returned as `false` rather than thrown:
   * the clinician is mid-review, not mid-request. A 401 `mfa_required` sends the
   * person to sign in afresh (the client does that), so it is not an error to say.
   */
  async function write(uid, run) {
    if (typeof uid !== 'string' || uid === '') return false;

    // First, before anything can wait: a Save asked for from here on waits for this.
    const release = hold(uid);

    clearError(uid);
    delete inflight[uid];

    try {
      await run();

      return true;
    } catch (error) {
      if (!(error instanceof ApiError)) {
        // eslint-disable-next-line no-console
        console.warn('[chartCdaStagedItems] a review change failed', error);
      }
      if (!(error instanceof ApiError && error.code === 'mfa_required')) {
        setError(uid, describe(error));
      }
      failures[uid] = (failures[uid] || 0) + 1;

      return false;
    } finally {
      release();
    }
  }

  /** The words for a failed change: the API's own when it gave any, else a plain one. */
  function describe(error) {
    const details = error instanceof ApiError ? error.details : null;

    if (details && typeof details === 'object') {
      const first = Object.values(details).flat().find((line) => typeof line === 'string');

      if (first) return first;
    }
    if (error instanceof ApiError && error.message) return error.message;

    return 'That change could not be made. Try again.';
  }

  /**
   * Correct staged rows (PATCH). `changes` is the API's own shape,
   * `{diagnoses: [{id, onset_at}]}`. The answer is the recomputed set, so the
   * entry is settled with it: the notes are read again without a reload.
   *
   * @returns {Promise<boolean>} whether the change was made
   */
  function correct(uid, changes) {
    return write(uid, async () => {
      const items = await updateStagedItems(uid, changes);

      settle(uid, { items: items || {}, failed: false, stale: false });
    });
  }

  /**
   * Leave one item out of the chart (DELETE staged-items/{kind}/{id}); the
   * answer is the set that remains. What the document's status became (saved,
   * if that was the last item of a partly saved one) is for the caller to read
   * again from the list.
   *
   * @returns {Promise<boolean>}
   */
  function drop(uid, kind, itemId) {
    return write(uid, async () => {
      const items = await dropStagedItem(uid, kind, itemId);

      settle(uid, { items: items || {}, failed: false, stale: false });
    });
  }

  /**
   * Discard every extracted item (DELETE staged-items). Nothing is left to
   * show, so the entry goes; the document's new status comes from the list.
   *
   * @returns {Promise<boolean>}
   */
  function discardAll(uid) {
    return write(uid, async () => {
      await discardStagedItems(uid);

      const next = { ...entries.value };
      delete next[uid];
      entries.value = next;
      requested.delete(uid);
    });
  }

  /**
   * Read the C-CDA again (POST scan): the document goes back to `scanning` and
   * its staged set is replaced. The old set goes from the screen.
   *
   * @returns {Promise<boolean>}
   */
  function rescan(uid) {
    return write(uid, async () => {
      await scanCdaDocument(uid);

      const next = { ...entries.value };
      delete next[uid];
      entries.value = next;
      requested.delete(uid);
    });
  }

  return {
    entries, errors, load, ensureFor, invalidate, clear, correct, drop, discardAll, rescan, hold, failureMark, settled,
  };
});
